import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { useQueryGetExamAssignment } from "@/apis/exam_assignments";
import { useQueryGetExamSet } from "@/apis/exam_sets/getExamSet";
import { useQueryGetExamAssignmentMonitor } from "@/apis/exam_assignments/getExamAssignmentMonitor";
import { getExamReport } from "@/apis/exam_papers/getExamReport";
import { useQueryGetCategories } from "@/apis/categories/getCategories";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/button";
import SATScoreReport from "../exam_reports/components/SATScoreReport";
import ACTScoreReport from "../exam_reports/components/ACTScoreReport";
import APScoreReport from "../exam_reports/components/APScoreReport";
import ScoreReportStudentFilterModal from "./components/ScoreReportStudentFilterModal";
import type { IExamReport } from "@/apis/exam_papers/getExamReport";
import { getPaperTypeLabel } from "@/constants/exam";

const ExamAssignmentPrintScoreReportsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Get exam assignment
  const {
    data: examAssignment,
    isLoading: isLoadingAssignment,
    error: assignmentError,
  } = useQueryGetExamAssignment(id!);

  // Get exam assignment monitor for student list
  const { data: examAssignmentMonitor } = useQueryGetExamAssignmentMonitor(
    id || ""
  );

  // Get exam set
  const { data: examSet, isLoading: isLoadingExamSet } = useQueryGetExamSet(
    examAssignment?.exam_set_id || "",
    {
      enabled: !!examAssignment?.exam_set_id,
    }
  );

  // Get categories for reports
  const { data: categories } = useQueryGetCategories();

  // Get available students with completed exams and generated reports
  const availableStudents = useMemo(() => {
    if (!examAssignmentMonitor?.progress_list) return [];
    
    return examAssignmentMonitor.progress_list
      .filter((item) => 
        item.progress.status === "completed" && 
        item.progress.exam_report !== null
      )
      .map((item) => {
        const report = item.progress.exam_report;
        const subjectScores = report?.subjects?.map((s: any) => ({
          subject: s.subject,
          score: s.total_score,
        })) || [];

        return {
          id: item.progress.id,
          userId: item.user.id,
          name: item.user.name,
          email: item.user.email,
          score: report?.total_score || 0,
          isRetake: item.progress.is_retake || false,
          subjectScores,
        };
      });
  }, [examAssignmentMonitor]);

  // Set default to all students
  useEffect(() => {
    if (availableStudents.length > 0 && selectedStudents.length === 0) {
      setSelectedStudents(availableStudents.map(student => student.id));
    }
  }, [availableStudents]);

  // Load exam reports for selected students using React Query
  const reportQueries = useQueries({
    queries: selectedStudents.map((progressId) => ({
      queryKey: ["examReport", progressId],
      queryFn: () => getExamReport(progressId),
      enabled: selectedStudents.length > 0,
    })),
  });

  const isLoadingReports = reportQueries.some((query) => query.isLoading);
  const examReports = reportQueries
    .map((query) => query.data)
    .filter((report): report is IExamReport => report !== undefined);


  const handleStudentFilterApply = (selectedIds: string[]) => {
    setSelectedStudents(selectedIds);
  };

  const isLoading = isLoadingAssignment || isLoadingExamSet;
  const error = assignmentError;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">
            Error Loading Exam Assignment
          </h2>
          <p className="text-gray-600">
            {error instanceof Error ? error.message : "An error occurred"}
          </p>
        </div>
      </div>
    );
  }

  if (!examAssignment || !examSet) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            Exam Assignment Not Found
          </h2>
          <p className="text-gray-500">
            The requested exam assignment could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Check if exam type is supported
  if (!["SAT", "ACT", "AP"].includes(examSet.exam_type)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            Not Supported
          </h2>
          <p className="text-gray-500">
            Score report printing is currently only supported for SAT, ACT, and AP exams.
          </p>
        </div>
      </div>
    );
  }

  if (availableStudents.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600 mb-2">
            No Score Reports Available
          </h2>
          <p className="text-gray-500">
            No students have completed exams with generated reports.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters - Hidden when printing */}
      <div className="print:hidden mb-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={() => setIsFilterModalOpen(true)}
            className="whitespace-nowrap"
          >
            Select Students ({selectedStudents.length}/{availableStudents.length})
          </Button>
        </div>
      </div>

      {/* Loading reports */}
      {isLoadingReports && (
        <div className="flex items-center justify-center py-8">
          <Spinner className="w-6 h-6 mr-2" />
          <span>Loading score reports...</span>
        </div>
      )}

      {/* Score Reports - Multiple reports for printing */}
      {!isLoadingReports && examReports.length > 0 && (
        <div className="print:block">
          {examReports.map((report, index) => (
            <div
              key={report.id}
              className={index > 0 ? "break-before-page" : ""}
            >
              {examSet.exam_type === "ACT" ? (
                <ACTScoreReport
                  examReport={report}
                  categories={categories || undefined}
                />
              ) : examSet.exam_type === "AP" ? (
                <APScoreReport
                  examReport={report}
                  categories={categories || undefined}
                />
              ) : (
                <SATScoreReport
                  examReport={report}
                  categories={categories || undefined}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* No students selected */}
      {!isLoadingReports && selectedStudents.length === 0 && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              No Students Selected
            </h2>
            <p className="text-gray-500">
              Please select at least one student to print score reports.
            </p>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      <ScoreReportStudentFilterModal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApply={handleStudentFilterApply}
        students={availableStudents}
        selectedStudentIds={selectedStudents}
      />
    </div>
  );
};

export default ExamAssignmentPrintScoreReportsPage;